import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GroupsIcon from "@mui/icons-material/Groups";
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "../../data/colors";
import { customStyles } from "../../data/styles/modalStyles";
import {
  ActiveChatTarget,
  ChatMessage,
  extendedReactions,
  quickReactions,
} from "./chatTypes";

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function MessageStatus({ message, me }: { message: ChatMessage; me: number }) {
  if (Number(message.REMCHA) !== me) return null;
  if (message.LEICHA) {
    return <DoneAllIcon style={{ fontSize: 14, color: colors.lightBlue }} />;
  }
  if (message.DELCHA) {
    return <DoneAllIcon style={{ fontSize: 14, color: colors.darkGrey }} />;
  }
  return <DoneIcon style={{ fontSize: 14, color: colors.darkGrey }} />;
}

function ReactionBadges({ message }: { message: ChatMessage }) {
  if (!message.reactions || message.reactions.length === 0) return null;
  const names = new Map<string, string[]>();
  message.reactions.forEach((r) => {
    const list = names.get(r.EMOCHA) || [];
    list.push(r.NOMUSU || "?");
    names.set(r.EMOCHA, list);
  });
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
      {Array.from(names.entries()).map(([emoji, reactors]) => (
        <span
          key={emoji}
          title={reactors.join(", ")}
          style={{
            backgroundColor: colors.white,
            borderRadius: 10,
            padding: "0 6px",
            fontSize: 12,
            boxShadow: "0 1px 1px rgba(0,0,0,0.15)",
            cursor: "default",
          }}
        >
          {emoji}
          {reactors.length > 1 ? ` ${reactors.length}` : ""}
        </span>
      ))}
    </div>
  );
}

function ReactionModal({
  open,
  canRemove,
  onClose,
  onPick,
  onRemove,
}: {
  open: boolean;
  canRemove: boolean;
  onClose: () => void;
  onPick: (emoji: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      onAfterClose={() => setExpanded(false)}
      style={customStyles({ z1: 90, z2: 91, maxHeight: 360 })}
    >
      <div style={{ width: 260 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <strong>{t("p.chatReact")}</strong>
          <CloseIcon className="noSelect" style={{ cursor: "pointer" }} onClick={onClose} />
        </div>

        {canRemove && (
          <div
            className="noSelect"
            onClick={onRemove}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              color: colors.red,
              padding: "6px 0",
              marginBottom: 6,
              borderBottom: `1px solid ${colors.lightGrey}`,
            }}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
            {t("p.chatRemoveReaction")}
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {quickReactions.map((emoji) => (
            <span
              key={emoji}
              className="noSelect"
              style={{ cursor: "pointer", fontSize: 22, padding: 4 }}
              onClick={() => onPick(emoji)}
            >
              {emoji}
            </span>
          ))}
          {!expanded && (
            <AddCircleOutlineIcon
              className="noSelect"
              style={{ fontSize: 22, color: colors.darkGrey, cursor: "pointer", padding: 4 }}
              onClick={() => setExpanded(true)}
            />
          )}
        </div>

        {expanded && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginTop: 8,
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            {extendedReactions.map((emoji) => (
              <span
                key={emoji}
                className="noSelect"
                style={{ cursor: "pointer", fontSize: 22, padding: 4 }}
                onClick={() => onPick(emoji)}
              >
                {emoji}
              </span>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DeleteConfirmModal({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={customStyles({ z1: 90, z2: 91, maxHeight: "auto" })}
    >
      <div style={{ width: 260 }}>
        <div style={{ marginBottom: 16 }}>{t("p.chatDeleteMessageConfirm")}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="noSelect" style={{ cursor: "pointer" }} onClick={onClose}>
            {t("p.cancel")}
          </button>
          <button
            className="noSelect"
            style={{ cursor: "pointer", color: colors.red }}
            onClick={onConfirm}
          >
            {t("p.delete")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function ChatThread({
  me,
  target,
  online,
  lastActive,
  messages,
  loading,
  hasMore,
  composerText,
  setComposerText,
  sending,
  sendError,
  onBack,
  onLoadOlder,
  onSend,
  onReact,
  onRemoveReaction,
  editingMessageId,
  onStartEdit,
  onCancelEdit,
  onDelete,
}: {
  me: number;
  target: ActiveChatTarget;
  online: boolean;
  lastActive: string | null;
  messages: ChatMessage[];
  loading: boolean;
  hasMore: boolean;
  composerText: string;
  setComposerText: (v: string) => void;
  sending: boolean;
  sendError: boolean;
  onBack: () => void;
  onLoadOlder: () => void;
  onSend: () => void;
  onReact: (codCha: number, emoji: string) => void;
  onRemoveReaction: (codCha: number) => void;
  editingMessageId: number | null;
  onStartEdit: (message: ChatMessage) => void;
  onCancelEdit: () => void;
  onDelete: (codCha: number) => void;
}) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [reactionModalFor, setReactionModalFor] = useState<number | null>(null);
  const [deleteConfirmFor, setDeleteConfirmFor] = useState<number | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const isBroadcastView = target.type === "roleBroadcast";
  const reactionModalMessage = messages.find((m) => m.CODCHA === reactionModalFor);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          backgroundColor: colors.chatPanelBg,
          borderBottom: `1px solid ${colors.lightGrey2}`,
        }}
      >
        <ArrowBackIcon
          className="noSelect"
          style={{ cursor: "pointer" }}
          onClick={onBack}
        />
        {isBroadcastView && <GroupsIcon style={{ color: colors.darkGrey }} />}
        <div>
          <strong>
            {isBroadcastView ? `${t("p.chatSentToRole")} ${target.name}` : target.name}
          </strong>
          {target.type === "user" && (
            <div style={{ fontSize: 11, color: colors.darkGrey }}>
              {online
                ? t("p.chatOnline")
                : lastActive
                  ? `${t("p.chatLastSeen")} ${formatTime(lastActive)}`
                  : ""}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 10,
          backgroundColor: colors.lightGrey3,
        }}
      >
        {loading && messages.length === 0 && (
          <div style={{ textAlign: "center", color: colors.darkGrey, fontSize: 13 }}>
            ...
          </div>
        )}

        {!isBroadcastView && messages.length === 0 && !loading && (
          <div style={{ textAlign: "center", color: colors.darkGrey, fontSize: 13, marginTop: 20 }}>
            {t("p.chatNoMessages")}
          </div>
        )}

        {!isBroadcastView && hasMore && (
          <div style={{ textAlign: "center", marginBottom: 8 }}>
            <button className="noSelect" onClick={onLoadOlder} style={{ cursor: "pointer" }}>
              {t("p.chatLoadMore")}
            </button>
          </div>
        )}

        {isBroadcastView
          ? messages.map((m) => (
              <div
                key={m.CODCHA}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 4px",
                  borderBottom: `1px solid ${colors.lightGrey}`,
                }}
              >
                <span>{m.DESNAME}</span>
                {m.LEICHA ? (
                  <DoneAllIcon style={{ color: colors.green, fontSize: 18 }} />
                ) : m.DELCHA ? (
                  <DoneAllIcon style={{ color: colors.darkGrey, fontSize: 18 }} />
                ) : (
                  <DoneIcon style={{ color: colors.darkGrey, fontSize: 18 }} />
                )}
              </div>
            ))
          : messages.map((m) => {
              const sent = Number(m.REMCHA) === me;
              const deleted = !!m.BORCHA;
              const canModify = sent && !m.LEICHA && !deleted;
              return (
                <div
                  key={m.CODCHA}
                  onMouseEnter={() => setHoveredId(m.CODCHA)}
                  onMouseLeave={() =>
                    setHoveredId((prev) => (prev === m.CODCHA ? null : prev))
                  }
                  style={{
                    display: "flex",
                    justifyContent: sent ? "flex-end" : "flex-start",
                    alignItems: "center",
                    gap: 4,
                    marginBottom: 6,
                  }}
                >
                  {sent && hoveredId === m.CODCHA && !deleted && (
                    <div style={{ display: "flex", gap: 4 }}>
                      {canModify && (
                        <>
                          <EditOutlinedIcon
                            className="noSelect"
                            style={{ fontSize: 16, color: colors.darkGrey, cursor: "pointer" }}
                            onClick={() => onStartEdit(m)}
                          />
                          <DeleteOutlineIcon
                            className="noSelect"
                            style={{ fontSize: 16, color: colors.darkGrey, cursor: "pointer" }}
                            onClick={() => setDeleteConfirmFor(m.CODCHA)}
                          />
                        </>
                      )}
                      <AddReactionOutlinedIcon
                        className="noSelect"
                        style={{ fontSize: 16, color: colors.darkGrey, cursor: "pointer" }}
                        onClick={() => setReactionModalFor(m.CODCHA)}
                      />
                    </div>
                  )}
                  <div style={{ maxWidth: "75%" }}>
                    <div
                      style={{
                        backgroundColor: sent
                          ? colors.chatSentBubble
                          : colors.chatReceivedBubble,
                        borderRadius: 8,
                        padding: "6px 10px",
                        boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                      }}
                    >
                      {m.ROLCHA && !sent && !deleted && (
                        <div style={{ fontSize: 10, color: colors.darkGrey }}>
                          {t("p.chatSentToRole")} {m.ROLNAME}
                        </div>
                      )}
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontStyle: deleted ? "italic" : "normal",
                          color: deleted ? colors.darkGrey : undefined,
                        }}
                      >
                        {deleted ? t("p.chatMessageDeleted") : m.MSGCHA}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 3,
                          fontSize: 10,
                          color: colors.darkGrey,
                        }}
                      >
                        {!deleted && m.EDICHA && (
                          <span style={{ fontStyle: "italic" }}>{t("p.chatEdited")}</span>
                        )}
                        {formatTime(m.FECCHA)}
                        <MessageStatus message={m} me={me} />
                      </div>
                    </div>
                    {!deleted && <ReactionBadges message={m} />}
                  </div>
                  {!sent && hoveredId === m.CODCHA && !deleted && (
                    <AddReactionOutlinedIcon
                      className="noSelect"
                      style={{ fontSize: 16, color: colors.darkGrey, cursor: "pointer" }}
                      onClick={() => setReactionModalFor(m.CODCHA)}
                    />
                  )}
                </div>
              );
            })}
        <div ref={bottomRef} />
      </div>

      {editingMessageId !== null && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            backgroundColor: colors.chatPanelBg,
            fontSize: 12,
            color: colors.darkGrey,
          }}
        >
          {t("p.chatEditingMessage")}
          <CloseIcon
            className="noSelect"
            style={{ fontSize: 16, cursor: "pointer" }}
            onClick={onCancelEdit}
          />
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 6,
          padding: 8,
          borderTop: `1px solid ${colors.lightGrey2}`,
        }}
      >
        <textarea
          value={composerText}
          onChange={(e) => setComposerText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("p.chatTypeMessage")}
          rows={1}
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 6,
            border: `1px solid ${colors.lightGrey2}`,
            padding: 8,
            fontFamily: "inherit",
          }}
        />
        <SendIcon
          className="noSelect"
          style={{
            cursor: sending ? "default" : "pointer",
            color: sending ? colors.grey : colors.green,
          }}
          onClick={() => !sending && onSend()}
        />
      </div>
      {sendError && (
        <div style={{ color: colors.red, fontSize: 12, padding: "0 8px 6px" }}>
          {t("p.chatSendError")}
        </div>
      )}

      <ReactionModal
        open={reactionModalFor !== null}
        canRemove={!!reactionModalMessage?.reactions?.some((r) => r.CODUSU === me)}
        onClose={() => setReactionModalFor(null)}
        onPick={(emoji) => {
          if (reactionModalFor !== null) onReact(reactionModalFor, emoji);
          setReactionModalFor(null);
        }}
        onRemove={() => {
          if (reactionModalFor !== null) onRemoveReaction(reactionModalFor);
          setReactionModalFor(null);
        }}
      />

      <DeleteConfirmModal
        open={deleteConfirmFor !== null}
        onClose={() => setDeleteConfirmFor(null)}
        onConfirm={() => {
          if (deleteConfirmFor !== null) onDelete(deleteConfirmFor);
          setDeleteConfirmFor(null);
        }}
      />
    </div>
  );
}
